import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import * as React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useTranslation } from 'react-i18next';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import { tagEvent } from 'utils/ga';
import { axiosInstance } from 'services/api';
import z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import clsx from 'clsx';

const MAX_NICKNAME_LENGTH = 45;

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'field_is_required' })
    .max(MAX_NICKNAME_LENGTH, { error: 'maximum_length_exceeded' }),
});

type FormData = z.infer<typeof schema>;

interface Props {
  closeDialog: () => void;
}

export function EditNickname(props: Props) {
  const { t } = useTranslation();

  const { equipmentListData: data, setEquipmentListData: setData } =
    React.useContext(EquipmentListContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const equipmentData = data.selectedEquipment as IEquipmentEntry;

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: (equipmentData.nickName || '').slice(0, MAX_NICKNAME_LENGTH),
    },
  });

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const handlePopState = () => props.closeDialog();

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [equipmentData]);

  const onSubmit = ({ name }: FormData) => {
    setIsLoading(true);
    axiosInstance
      .put(
        `/api/user/equipment/nickName`,
        JSON.stringify({
          id: equipmentData.id,
          nickName: name,
        }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then(() => {
        setData(oldData => ({
          ...oldData,
          selectedEquipment: {
            ...(oldData.selectedEquipment as IEquipmentEntry),
            nickName: name,
          },
          equipmentList: oldData.equipmentList!.map((equip, index) => {
            if (index === oldData.selectedIndex) {
              return {
                ...equip,
                nickName: name,
              };
            }
            return equip;
          }),
        }));
        tagEvent('edit_nickname');
        props.closeDialog();
      })
      .catch(e => {
        addErrorModal();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            props.closeDialog();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-name="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          {equipmentData.nickName ? (
            <h2>{t('edit_nickname')}</h2>
          ) : (
            <h2>{t('add_nickname')}</h2>
          )}
          <div className="form-caption" style={{ marginBottom: '32px' }}>
            <div className="inline-label-text">
              <span
                className="label"
                style={{
                  fontWeight: '400',
                  minWidth: '0px',
                  paddingRight: '0px',
                }}
              >
                {t('equipment_model')}:
              </span>{' '}
              <span className="text" style={{ fontWeight: '500' }}>
                {equipmentData.model}
              </span>
            </div>
            <div className="inline-label-text">
              <span
                className="label"
                style={{
                  fontWeight: '400',
                  minWidth: '0px',
                  paddingRight: '0px',
                }}
              >
                {equipmentData.identifierType === 'Pin'
                  ? t('equipment_pin')
                  : t('equipment_serial')}
                :
              </span>{' '}
              <span className="text" style={{ fontWeight: '500' }}>
                {equipmentData.pinOrSerial}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-group', { error: !!errors.name })}>
              <div className="form-content">
                <div className="form-label">
                  <span>{t('nickname')}</span>
                </div>
                <div className="form-control">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('enter_the_new_nickname')}
                        disabled={isLoading}
                        onChange={e => {
                          const nextValue = String(e.value ?? '');
                          field.onChange(
                            nextValue.slice(0, MAX_NICKNAME_LENGTH),
                          );
                        }}
                      />
                    )}
                  />
                </div>
              </div>
              {errors.name?.message && (
                <div className="form-error">
                  <Error>
                    {t<string>(errors.name.message, {
                      length: MAX_NICKNAME_LENGTH,
                    })}
                  </Error>
                </div>
              )}
            </div>
            <Button themeColor="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save_changes')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
